import React, { useState, useEffect } from 'react';
import pushNotificationManager from '../utils/pushNotificationManager';
import './PushNotificationPrompt.css';

const PushNotificationPrompt = () => {
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('🔔 [PushNotificationPrompt] Component mounted');
        checkSubscriptionStatus();
    }, []);

    const checkSubscriptionStatus = async () => {
        try {
            console.log('🔔 [PushNotificationPrompt] Checking notification support...');
            const supported = pushNotificationManager.isNotificationSupported();
            console.log('🔔 [PushNotificationPrompt] Notifications supported:', supported);
            
            if (!supported) {
                console.warn('🔔 [PushNotificationPrompt] Push notifications not supported on this device/browser');
                return;
            }

            const subscribed = await pushNotificationManager.isSubscribed();
            console.log('🔔 [PushNotificationPrompt] Is subscribed:', subscribed);
            setIsSubscribed(subscribed);
            
            // Show prompt if not subscribed and not previously dismissed
            const dismissed = localStorage.getItem('push-prompt-dismissed');
            console.log('🔔 [PushNotificationPrompt] Prompt dismissed:', dismissed);
            
            if (!subscribed && !dismissed) {
                console.log('🔔 [PushNotificationPrompt] Showing subscription prompt');
                setShowPrompt(true);
            } else if (subscribed) {
                console.log('🔔 [PushNotificationPrompt] User is subscribed, showing status bar');
            } else {
                console.log('🔔 [PushNotificationPrompt] Prompt was dismissed, not showing anything');
            }
        } catch (error) {
            console.error('🔔 [PushNotificationPrompt] Error checking subscription status:', error);
        }
    };

    const handleSubscribe = async () => {
        setIsLoading(true);
        setError(null);

        try {
            await pushNotificationManager.subscribe();
            setIsSubscribed(true);
            setShowPrompt(false);
            localStorage.removeItem('push-prompt-dismissed');
        } catch (error) {
            console.error('Failed to subscribe to push notifications:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUnsubscribe = async () => {
        setIsLoading(true);
        setError(null);

        try {
            await pushNotificationManager.unsubscribe();
            setIsSubscribed(false);
        } catch (error) {
            console.error('Failed to unsubscribe from push notifications:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('push-prompt-dismissed', 'true');
    };

    const handleTestNotification = async () => {
        try {
            await pushNotificationManager.showTestNotification();
        } catch (error) {
            console.error('Failed to show test notification:', error);
            setError(error.message);
        }
    };

    const handleClearNotifications = async () => {
        console.log('🔔 [PushNotificationPrompt] Clearing notifications...');
        try {
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.ready;
                // Send message to service worker to clear notifications
                if (registration.active) {
                    registration.active.postMessage({ type: 'CLEAR_BADGE' });
                }
            }
        } catch (error) {
            console.error('🔔 [PushNotificationPrompt] Failed to clear notifications:', error);
            setError(error.message);
        }
    };

    if (!pushNotificationManager.isNotificationSupported()) {
        return null;
    }

    if (showPrompt && !isSubscribed) {
        return (
            <div className="push-prompt-overlay">
                <div className="push-prompt">
                    <div className="push-prompt-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" fill="currentColor"/>
                        </svg>
                    </div>
                    <h3>Enable Notifications</h3>
                    <p>Get notified when users request PIN codes</p>
                    
                    {error && (
                        <div className="push-prompt-error">
                            {error}
                        </div>
                    )}
                    
                    <div className="push-prompt-actions">
                        <button
                            className="push-prompt-btn push-prompt-btn-primary"
                            onClick={handleSubscribe}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Enabling...' : 'Enable'}
                        </button>
                        <button
                            className="push-prompt-btn push-prompt-btn-secondary"
                            onClick={handleDismiss}
                            disabled={isLoading}
                        >
                            Not Now
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show status indicator for subscribed users
    if (isSubscribed) {
        return (
            <div className="push-status">
                <div className="push-status-content">
                    <span className="push-status-indicator">🔔</span>
                    <span className="push-status-text">Notifications enabled</span>
                    <button
                        className="push-status-btn"
                        onClick={handleUnsubscribe}
                        disabled={isLoading}
                    >
                        Disable
                    </button>
                    <button
                        className="push-status-btn push-status-btn-test"
                        onClick={handleTestNotification}
                    >
                        Test
                    </button>
                    <button
                        className="push-status-btn push-status-btn-clear"
                        onClick={handleClearNotifications}
                        title="Clear all notifications and badge"
                    >
                        Clear
                    </button>
                </div>
                {error && (
                    <div className="push-status-error">
                        {error}
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default PushNotificationPrompt;
